import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ProductGrid } from '../components/products/ProductGrid';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Category = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Try to get category directly by slug/name
        try {
          const catRes = await axios.get(`${API}/categories/${slug}`);
          if (catRes.data && catRes.data.id) {
            setCategory(catRes.data);
            // Get products for this category
            const prodRes = await axios.get(`${API}/products?category_id=${catRes.data.id}`);
            setProducts(prodRes.data);
            return;
          }
        } catch (e) {
          // If direct lookup fails, fall back to searching all categories
          console.log('Direct category lookup failed, trying search...');
        }
        
        // Fallback: Get all categories and search locally
        const catRes = await axios.get(`${API}/categories`);
        const matchedCategory = catRes.data.find((c) => {
          const catNameLower = c.name.toLowerCase();
          const catSlug = c.name.toLowerCase().replace(/\s+/g, '-');
          const searchSlug = slug.toLowerCase();
          
          return catNameLower === searchSlug || 
                 catSlug === searchSlug ||
                 catNameLower === searchSlug.replace(/-/g, ' ');
        });
        
        if (matchedCategory) {
          setCategory(matchedCategory);
          const prodRes = await axios.get(`${API}/products?category_id=${matchedCategory.id}`);
          setProducts(prodRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch category:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const bestsellers = products.filter((p) => p.is_bestseller);
  const regularProducts = products.filter((p) => !p.is_bestseller);

  const displayProducts = activeTab === 'bestsellers' ? bestsellers : 
    activeTab === 'regular' ? regularProducts : products;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-muted rounded w-32" />
          <div className="h-48 bg-muted rounded-lg" />
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="product-grid">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-muted rounded-lg h-80" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="font-serif text-2xl font-bold text-foreground mb-4">Category Not Found</h1>
        <p className="text-muted-foreground mb-6">The category you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/products">
          <Button>Browse All Products</Button>
        </Link>
      </div>
    );
  }

  const categoryImages = {
    'Books': 'https://images.pexels.com/photos/9223391/pexels-photo-9223391.jpeg',
    'Crystals': 'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg',
    'Incense': 'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg',
    'Soaps': 'https://images.unsplash.com/photo-1730378616989-499041edc37a',
    'Toys': 'https://images.pexels.com/photos/31061855/pexels-photo-31061855.jpeg',
    'Miscellaneous': 'https://images.pexels.com/photos/32533253/pexels-photo-32533253.jpeg',
  };

  return (
    <div data-testid="category-page">
      {/* Hero Banner */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src={category.image_url || categoryImages[category.name] || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
          alt={category.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-2 text-center">
            {category.name}
          </h1>
          <p className="text-white/90 text-center max-w-xl">
            {category.description}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link to="/products" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to All Products
        </Link>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList data-testid="category-tabs">
            <TabsTrigger value="all" data-testid="tab-all">
              All ({products.length})
            </TabsTrigger>
            <TabsTrigger value="bestsellers" data-testid="tab-bestsellers" className="flex items-center gap-1">
              <Star className="h-4 w-4" />
              Bestsellers ({bestsellers.length})
            </TabsTrigger>
            <TabsTrigger value="regular" data-testid="tab-regular">
              Other ({regularProducts.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Products */}
        <ProductGrid products={displayProducts} loading={false} />
      </div>
    </div>
  );
};

export default Category;
